import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CursorListState } from '../../lib/list/useCursorList'
import { useCursorList } from '../../lib/list/useCursorList'
import { RFQ_LIST_PAGE_SIZE, tabBadgeCountsFromStatusCounts } from './domain'
import type { Rfq, RfqListResponse, RfqServerTabId, RfqStatusCounts, RfqTabId } from './domain'
import { defaultRfqListService } from './rfq-list-service'
import type { RfqListService } from './rfq-list-service'

const SERVER_TABS: readonly RfqServerTabId[] = ['requested', 'quoted', 'expired']

export interface RfqTabListResult {
  readonly state: CursorListState<RfqListResponse>
  readonly loadMore: () => void
  /** Code-review W4 — re-fetches THIS tab's page 1 (retry-after-error), same
   * `useCursorList.reload` every other tab-list result already threads
   * through. A lazily-visited tab (D6 row 2's per-tab lazy fetch) that
   * fails its first fetch used to have no retry affordance of its own —
   * only the page-level panel's `reloadAll` had one, and that's gated on
   * `allTabsErrored` (every visited tab failing), so a single failed tab
   * alongside a healthy one showed a dead-end inline error. */
  readonly reload: () => void
}

export interface UseRfqListResult {
  readonly byTab: Readonly<Record<RfqServerTabId, RfqTabListResult>>
  /** `null` until at least one tab's first page has resolved. Every tab's
   * first page carries the SAME account-wide value (D5) — the first one to
   * arrive wins; they should never disagree. */
  readonly statusCounts: RfqStatusCounts | null
  /** `statusCounts` folded through `tabForStatus` (D5) — the 3 server tab
   * badges. `{}`-safe default (all zero) until `statusCounts` resolves. */
  readonly tabBadgeCounts: Record<RfqServerTabId, number>
  /** True until the ANCHOR tab's first page has settled (ready or error) —
   * drives the page's initial "Loading your RFQs…" state and gates default-
   * tab selection / scroll restoration, which both need `statusCounts`. A
   * not-yet-visited tab is `idle`, not pending: it is never waited on. */
  readonly initialLoadPending: boolean
  /** True only if every tab FETCHED so far failed its first page — i.e. the
   * screen has no `statusCounts` at all and so cannot render its tab strip.
   * A single failed tab alongside a healthy one is that tab's own inline
   * error, not a page-level one. */
  readonly allTabsErrored: boolean
  /** Re-fetches page 1 of every tab fetched so far (retry-after-error, or a
   * refresh). Never wakes a tab the user has not visited. */
  readonly reloadAll: () => void
}

/**
 * UID-603 (ADR-0176 D5/D6 row 2) — each of the three server tabs is
 * independently cursor-paginated at `RFQ_LIST_PAGE_SIZE` through the SAME
 * shared `useCursorList` hook `usePartsLibrary` builds on (D1.4 — "one
 * frontend hook, not two").
 *
 * Tabs are fetched LAZILY, one per visit (D6 row 2, `useCursorList`'s
 * `enabled` flag): on mount only the tab the page opens on issues a request,
 * and each other tab issues its first page the first time it is actually
 * visited. Once a tab has loaded, the visited set only ever grows, so its
 * `enabled` stays true and `useCursorList`'s first-page effect never re-runs
 * — switching away and back shows the already-loaded page (and its scroll
 * position's worth of appended pages) rather than refetching. The only
 * refetch path is the explicit `reloadAll` the page's error-panel "Try
 * again" calls; there is no other refresh affordance on this screen.
 *
 * The prior revision fetched all three eagerly on mount for a simpler state
 * machine. That cost 2 unnecessary requests on every single visit to `/rfq`,
 * which the "avoid redundant calls" requirement for this feature rules out —
 * and the simplification it bought is cheap to recover, because the ONE thing
 * the page needs before it can render anything (`statusCounts`) is
 * page-independent and account-wide (D5): it is identical on all three tabs'
 * first pages, so ONE fetched tab supplies every badge, the tab-strip
 * visibility gate and the default-tab pick. Badges therefore do NOT depend on
 * having fetched all three tabs' item lists — they never read
 * `items.length` — and are unaffected by this change.
 *
 * `activeTab` is the page's current tab, `RfqTabId` (draft included) rather
 * than `RfqServerTabId`, because the Draft tab is a client-bundle surface
 * with no server read of its own. Landing on it still needs `statusCounts`
 * for the badges, so `requested` anchors the screen in that case — the
 * mount-time request count is exactly one either way.
 */
export function useRfqList(
  activeTab: RfqTabId,
  service: RfqListService = defaultRfqListService,
): UseRfqListResult {
  const fetchers = useMemo(
    () =>
      Object.fromEntries(
        SERVER_TABS.map((tab) => [
          tab,
          (opts: { readonly cursor?: string; readonly limit: number }) =>
            service.listRfqs({ tab }, opts),
        ]),
      ) as Record<
        RfqServerTabId,
        (opts: { cursor?: string; limit: number }) => Promise<RfqListResponse>
      >,
    [service],
  )
  const getId = useCallback((rfq: Rfq) => rfq.id, [])

  // The tab whose read anchors the screen: the active one, or `requested`
  // when the page opens on the server-read-less Draft tab (see the doc
  // comment). Captured in a ref because it must not move when the customer
  // switches tabs — `initialLoadPending` is about the page's FIRST load, and
  // re-pointing it at a freshly-visited (`loading`) tab would put the whole
  // page back into its "Loading your RFQs…" state on every tab switch,
  // throwing away the tab strip and the already-rendered rows.
  const anchorTabRef = useRef<RfqServerTabId>(activeTab === 'draft' ? 'requested' : activeTab)
  // Monotonic: a tab enters on first visit and never leaves, which is what
  // makes a revisit show cached rows instead of refetching.
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<RfqServerTabId>>(
    () => new Set([anchorTabRef.current]),
  )
  useEffect(() => {
    if (activeTab === 'draft' || visitedTabs.has(activeTab)) return
    setVisitedTabs((prev) => new Set(prev).add(activeTab))
  }, [activeTab, visitedTabs])

  // Rules of Hooks forbid calling `useCursorList` in a loop over `SERVER_TABS`
  // — there are exactly 3 server tabs (a closed, frozen set, `domain.ts`'s
  // `RfqServerTabId`), so 3 fixed call sites is the correct fixed-cardinality
  // shape, not a workaround.
  const requested = useCursorList<Rfq, RfqListResponse>(fetchers.requested, {
    getId,
    pageSize: RFQ_LIST_PAGE_SIZE,
    enabled: visitedTabs.has('requested'),
    resetKey: [],
  })
  const quoted = useCursorList<Rfq, RfqListResponse>(fetchers.quoted, {
    getId,
    pageSize: RFQ_LIST_PAGE_SIZE,
    enabled: visitedTabs.has('quoted'),
    resetKey: [],
  })
  const expired = useCursorList<Rfq, RfqListResponse>(fetchers.expired, {
    getId,
    pageSize: RFQ_LIST_PAGE_SIZE,
    enabled: visitedTabs.has('expired'),
    resetKey: [],
  })

  const byTab: Record<RfqServerTabId, RfqTabListResult> = {
    requested: { state: requested.state, loadMore: requested.loadMore, reload: requested.reload },
    quoted: { state: quoted.state, loadMore: quoted.loadMore, reload: quoted.reload },
    expired: { state: expired.state, loadMore: expired.loadMore, reload: expired.reload },
  }

  const statusCounts: RfqStatusCounts | null =
    (requested.state.status === 'ready' ? requested.state.page.statusCounts : undefined) ??
    (quoted.state.status === 'ready' ? quoted.state.page.statusCounts : undefined) ??
    (expired.state.status === 'ready' ? expired.state.page.statusCounts : undefined) ??
    null

  const tabBadgeCounts = useMemo(
    () =>
      statusCounts
        ? tabBadgeCountsFromStatusCounts(statusCounts)
        : { requested: 0, quoted: 0, expired: 0 },
    [statusCounts],
  )

  // Only the anchor tab is waited on. An unvisited tab sits at `idle`
  // forever, so the eager version's "some tab is loading OR idle" would
  // never clear.
  const anchorState = byTab[anchorTabRef.current].state
  const initialLoadPending = anchorState.status === 'loading' || anchorState.status === 'idle'
  const allTabsErrored = SERVER_TABS.filter((tab) => visitedTabs.has(tab)).every(
    (tab) => byTab[tab].state.status === 'error',
  )

  const reloads: Record<RfqServerTabId, () => void> = {
    requested: requested.reload,
    quoted: quoted.reload,
    expired: expired.reload,
  }
  const reloadsRef = useRef(reloads)
  reloadsRef.current = reloads
  const reloadAll = useCallback(() => {
    for (const tab of SERVER_TABS) {
      // A `reload()` on a disabled tab would only bump a generation counter
      // `useCursorList`'s effect ignores while `enabled` is false — harmless,
      // but skipping it keeps "never fetch an unvisited tab" true at this
      // seam rather than relying on the other hook to swallow it.
      if (visitedTabs.has(tab)) reloadsRef.current[tab]()
    }
  }, [visitedTabs])

  return { byTab, statusCounts, tabBadgeCounts, initialLoadPending, allTabsErrored, reloadAll }
}
